import { Navigate, Outlet } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  Users,
  Bell,
  Menu,
  X,
  ChevronDown,
  LogOut,
  Home,
  Archive,
  UserMinus2,
  Rss,
  Book,
  Boxes,
} from "lucide-react";

import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../hooks/useAuth";

import Sidebar from "../components/dashboard/Sidebar";
import useWidth from "@/hooks/useWidth";
import menuItemsAll from "@/lib/routes";
import { EMAIL_ACCESS_RULES } from "@/lib/constants";

const ProtectedLayout = () => {
  const location = useLocation();
  const paths = location.pathname.split("/");
  const { width, breakpoints } = useWidth();
  const [sidebarOpen, setSidebarOpen] = useState(width >= breakpoints.md),
    [profileDropdownOpen, setProfileDropdownOpen] = useState(false),
    [notificationsOpen, setNotificationsOpen] = useState(false),
    [activeMenu, setActiveMenu] = useState("dashboard"),
    [activeSubMenu, setActiveSubMenu] = useState("");

  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  // Build filtered menu based on user email permissions
  const { filteredMenuItems, defaultPath } = useMemo(() => {
    const email = user?.email?.toLowerCase() || "";
    const rule = EMAIL_ACCESS_RULES[email];

    const items = rule
      ? menuItemsAll.filter((item) => rule.allowedMenuIds.includes(item.id))
      : menuItemsAll;

    return { filteredMenuItems: items, defaultPath: rule?.defaultPath ?? null };
  }, [user]);

  // Sync active menu/submenu with location and filtered items
  useEffect(() => {
    const activePath = filteredMenuItems
      .slice(1)
      .find((item) => paths.includes(item.path));
    setActiveMenu(activePath ? activePath.id : (filteredMenuItems[0]?.id || "dashboard"));

    const activeSub = filteredMenuItems
      .flatMap((item) => item.children || [])
      .find((item) => paths.includes(item.path));
    setActiveSubMenu(activeSub ? activeSub.id : "");
  }, [location.pathname, filteredMenuItems]);

  // Redirect to default allowed path if current path is not permitted
  useEffect(() => {
    if (!defaultPath) return; // full access users
    const isPermitted = filteredMenuItems.some((item) =>
      location.pathname.includes(`/dashboard/${item.path}`)
    );
    if (!isPermitted) {
      navigate(defaultPath, { replace: true });
    }
  }, [defaultPath, filteredMenuItems, location.pathname, navigate]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
    if (notificationsOpen) setNotificationsOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
  // Handled silently to avoid noisy logs; UX shows current page
    }
  };

  return (
    <div className="md:min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50 flex flex-col">
      {/* Top Navigation Bar */}
      <header className="bg-gradient-to-r from-rose-100 to-pink-100 border-b border-rose-200 z-30 fixed top-0 left-0 right-0 shadow-sm">
        <div className="flex items-center justify-between px-4 py-2">
          {/* Left side - Logo and menu toggle */}
          <div className="flex items-center">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-md text-rose-600 hover:text-rose-800 hover:bg-rose-200 focus:outline-none"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="ml-4 flex items-center">
              <span className="ml-2 text-lg font-semibold text-rose-800">
                GENES Mobile App
              </span>
            </div>
          </div>

          {/* Right side - Search, Notifications, Profile */}
          <div className="flex items-center space-x-3">
            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={toggleProfileDropdown}
                className="flex items-center space-x-2 p-1 rounded-full hover:bg-rose-200 focus:outline-none"
              >
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-rose-400 to-pink-400 flex items-center justify-center text-white font-medium shadow-sm">
                  {(() => {
                    const email = user?.email?.toLowerCase() || "";
                    if (email === "superadmin@qjskills.com") return "SA";
                    if (email === "admin@qjskills.com") return "A";
                    if (email === "feeds@qjskills.com") return "F";
                    if (email === "notification@qjskills.com") return "N";
                    // Default to first letter of name for regular users/admins
                    return user?.name?.charAt(0)?.toUpperCase() || "A";
                  })()}
                </div>
                <ChevronDown size={16} className="text-rose-600" />
              </button>

              {/* Profile Menu */}
              <AnimatePresence>
                {profileDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200"
                  >
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-800">
                        {(() => {
                          const email = user?.email?.toLowerCase() || "";
                          if (email === "superadmin@qjskills.com") return "Super Admin";
                          if (email === "admin@qjskills.com") return "Admin";
                          if (email === "feeds@qjskills.com") return "Feed Manager";
                          if (email === "notification@qjskills.com") return "Notification Manager";
                          // Default to actual name for regular users/admins
                          return user?.name || "Admin User";
                        })()}
                      </p>
                      <p className="text-xs text-gray-500">
                        {user?.email || "admin@example.com"}
                      </p>
                    </div>
                    <div className="border-t border-gray-200"></div>
                    <button
                      onClick={handleLogout}
                      className=" w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
                    >
                      <LogOut size={16} className="mr-2" />
                      Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar and Main Content */}
      <div className="flex pt-14 flex-1">
        {/* Sidebar */}
        <Sidebar
          sidebarOpen={sidebarOpen}
          menuItems={filteredMenuItems}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
          activeSubMenu={activeSubMenu}
          setActiveSubMenu={setActiveSubMenu}
          setSidebarOpen={setSidebarOpen}
          width={width}
        />

        {/* Main Content */}
        <main
          className={`flex-1 py-6 md:px-6 max-w-[90%] mx-auto transition-all duration-200 ${
            sidebarOpen ? "md:ml-64" : "md:ml-0"
          }`}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ProtectedLayout;
